import { solarData } from './SolarData';
import { FinanceData } from './FinanceData';

/**
 * Hardware promotions.
 *
 * These replace the 30% federal tax credit the flow used to quote against. The
 * difference matters: a tax credit arrived months later and was never ours to
 * promise, whereas a promotion comes off the price at checkout. So a promotion
 * reduces the amount financed and the monthly payment, not just the headline.
 *
 * A promotion is earned by the *configuration*, not by the package a customer
 * clicked. Someone who assembles a qualifying system by hand gets the discount
 * without ever touching a package card, and someone who drifts off-spec loses it
 * and falls back to standard pricing.
 */

/* Names are the join key between here and SolarData, so they live in one place
   rather than being retyped at every comparison. */
export const MODULE = {
  SUNKET: 'Sunket 455W',
  QCELL: 'QCell 410W',
};

export const INVERTER = {
  FOXESS: 'FoxEss PowerQ Inverter',
  SOLAREDGE: 'SolarEdge 11.4kW Inverter',
  ENPHASE: 'Enphase IQ8+',
  TESLA: 'Tesla Inverter',
};

export const BATTERY = {
  FOXESS: 'FoxESS PowerQ Battery',
  POWERWALL: 'Tesla Powerwall 3',
};

/* The FoxESS threshold counts batteries rather than kilowatt-hours on purpose.
   A unit is 3.97 kWh, so five come to 19.85 — a rule written as "20 kWh" would
   miss by 0.15 and silently demand a sixth battery. */
export const FOX_MIN_BATTERIES = 5;
export const FOX_MIN_SYSTEM_WATTS = 10500;
export const FOX_DISCOUNT = 4000;

export const PARTICIPATE_POWERWALL_RATE = 0.3;
export const PARTICIPATE_SOLAREDGE_RATE = 0.25;

/**
 * The Participate discounts are only available paying cash or financing through
 * this lender. Financing anywhere else forfeits them, so the flow picks the
 * lender from the promotion rather than letting a customer lose 30% by choosing
 * a dropdown option.
 */
export const PARTICIPATE_LENDER = 'Credit Human';
export const DEFAULT_LENDER = 'Climate First Bank';

const findBy = (options, name) => options.find((option) => option.name === name);

export const moduleNamed = (name) => findBy(solarData.modules.options, name);
export const inverterNamed = (name) => findBy(solarData.inverters.options, name);
export const batteryNamed = (name) => findBy(solarData.batteries.options, name);

/**
 * The four packages, in the order they are offered.
 *
 * `promotion` is the discount attached to the package, or null. Core carries
 * none — it is the streamlined entry system, priced as standard — so anything
 * that walks this list looking for a discount has to skip it rather than treat
 * "no conditions" as "all conditions met".
 */
export const PACKAGES = [
  {
    id: 'core',
    name: 'Core',
    subtitle: 'Smart, streamlined solar',
    promotion: null,
    hardware: {
      module: MODULE.SUNKET,
      inverter: INVERTER.FOXESS,
      battery: BATTERY.FOXESS,
      batteryQuantity: 0,
    },
  },
  {
    id: 'performance',
    name: 'Performance',
    subtitle: 'Great for houses with some shade',
    promotion: {
      label: 'Participate',
      headline: '25% off',
      rate: PARTICIPATE_SOLAREDGE_RATE,
      lender: PARTICIPATE_LENDER,
    },
    hardware: {
      module: MODULE.QCELL,
      inverter: INVERTER.SOLAREDGE,
      battery: BATTERY.FOXESS,
      batteryQuantity: 0,
    },
  },
  {
    id: 'reserve',
    name: 'Reserve',
    subtitle: 'Tesla Powerwall 3 package',
    promotion: {
      label: 'Participate',
      headline: '30% off',
      rate: PARTICIPATE_POWERWALL_RATE,
      lender: PARTICIPATE_LENDER,
    },
    hardware: {
      module: MODULE.QCELL,
      inverter: INVERTER.TESLA,
      battery: BATTERY.POWERWALL,
      batteryQuantity: 1,
    },
  },
  {
    id: 'independence',
    name: 'Independence',
    subtitle: 'Solar and full home battery backup',
    promotion: { label: 'FoxESS bundle', headline: '$4,000 off', amount: FOX_DISCOUNT },
    hardware: {
      module: MODULE.SUNKET,
      inverter: INVERTER.FOXESS,
      battery: BATTERY.FOXESS,
      batteryQuantity: FOX_MIN_BATTERIES,
    },
  },
];

export const packageById = (id) => PACKAGES.find((pkg) => pkg.id === id) || null;

/**
 * Resolves a package's hardware names into the option objects the rest of the
 * app stores on `hardware`. Deliberately leaves `systemSize` alone: that comes
 * from the customer's own bill, and quietly enlarging their array to reach a
 * promotion threshold would be selling them power they didn't ask for. The
 * eligibility list tells them they're short instead.
 */
export const packageHardware = (pkg) => ({
  module: moduleNamed(pkg.hardware.module),
  inverter: inverterNamed(pkg.hardware.inverter),
  battery: batteryNamed(pkg.hardware.battery),
  batteryQuantity: pkg.hardware.batteryQuantity,
});

/**
 * Every condition for one package, each carrying the sentence a customer would
 * need to read to fix it. The UI renders this list directly, so a rule and its
 * remedy can never drift apart.
 */
export function packageRules(packageId, hardware) {
  const moduleName = hardware?.module?.name;
  const inverterName = hardware?.inverter?.name;
  const batteryName = hardware?.battery?.name;
  const quantity = Number(hardware?.batteryQuantity) || 0;
  const watts = Number(hardware?.systemSize) || 0;

  switch (packageId) {
    case 'independence': {
      const shortBy = FOX_MIN_BATTERIES - quantity;
      return [
        {
          label: 'Sunket or QCell modules',
          met: moduleName === MODULE.SUNKET || moduleName === MODULE.QCELL,
          fix: 'Choose the Sunket 455W or QCell 410W module',
        },
        {
          label: 'FoxESS PowerQ inverter',
          met: inverterName === INVERTER.FOXESS,
          fix: 'Switch your inverter to the FoxESS PowerQ',
        },
        // Five units, not a kWh figure — five FoxESS batteries come to 19.85 kWh.
        {
          label: `${FOX_MIN_BATTERIES} FoxESS batteries`,
          met: batteryName === BATTERY.FOXESS && quantity >= FOX_MIN_BATTERIES,
          fix:
            batteryName !== BATTERY.FOXESS
              ? 'Switch your battery to the FoxESS PowerQ'
              : `Add ${shortBy} more batter${shortBy === 1 ? 'y' : 'ies'}`,
        },
        {
          label: '10.5 kW system or larger',
          met: watts >= FOX_MIN_SYSTEM_WATTS,
          fix: 'Increase your system size to 10.5 kW',
        },
      ];
    }
    case 'reserve':
      return [
        {
          label: 'Sunket or QCell modules',
          met: moduleName === MODULE.SUNKET || moduleName === MODULE.QCELL,
          fix: 'Choose the Sunket 455W or QCell 410W module',
        },
        {
          label: 'Tesla inverter',
          met: inverterName === INVERTER.TESLA,
          fix: 'Switch your inverter to the Tesla Inverter',
        },
        {
          label: 'Tesla Powerwall 3',
          met: batteryName === BATTERY.POWERWALL && quantity > 0,
          fix: 'Add at least one Tesla Powerwall 3',
        },
      ];
    case 'performance':
      return [
        {
          label: 'QCell 410W modules',
          met: moduleName === MODULE.QCELL,
          fix: 'Choose the QCell 410W module',
        },
        {
          label: 'SolarEdge 11.4kW inverter',
          met: inverterName === INVERTER.SOLAREDGE,
          fix: 'Switch your inverter to the SolarEdge 11.4kW',
        },
      ];
    // Core has no promotion, so it has no conditions to meet.
    default:
      return [];
  }
}

/** What a package is worth against a given subtotal, or null if it carries nothing. */
export function packagePromotion(packageId, subtotal) {
  const pkg = packageById(packageId);
  if (!pkg || !pkg.promotion) return null;

  const base = Number(subtotal) || 0;
  // A flat discount is capped at the subtotal so a small system can never price
  // below zero; a rate can't.
  const amount =
    pkg.promotion.amount != null
      ? Math.min(pkg.promotion.amount, base)
      : base * pkg.promotion.rate;

  return {
    id: pkg.id,
    name: pkg.name,
    label: pkg.promotion.label,
    headline: pkg.promotion.headline,
    // Which lender this price has to be financed through to survive. Absent
    // means any — the FoxESS bundle is ours, not Participate's.
    lender: pkg.promotion.lender || null,
    amount: Math.max(0, amount),
  };
}

/** The lender a promotion requires, falling back to the standard one. */
export const lenderForPromotion = (promotion) => promotion?.lender || DEFAULT_LENDER;

/** That lender's finance product, for pricing a monthly payment against it. */
export const financeProductFor = (lenderName) => {
  const entry = FinanceData.Lenders.find((lender) => lender[lenderName]);
  return entry ? entry[lenderName][0] : null;
};

/**
 * The promotion this build has actually earned, or null.
 *
 * Only packages that carry one are considered. Core's rule list is empty, and
 * `[].every(...)` is true — so without the filter every build would "match"
 * Core first and come away with nothing.
 *
 * The three that do carry a promotion are mutually exclusive by inverter, so at
 * most one can ever match and the iteration order carries no precedence.
 */
export function resolvePromotion(hardware, subtotal) {
  // Promotions are solar offers. A battery- or charger-only order has no system
  // to discount, so nothing applies.
  if (!(Number(hardware?.systemSize) > 0)) return null;

  const won = PACKAGES.filter((pkg) => pkg.promotion).find((pkg) =>
    packageRules(pkg.id, hardware).every((rule) => rule.met),
  );
  return won ? packagePromotion(won.id, subtotal) : null;
}

/**
 * True when the battery and the inverter are one piece of hardware — the FoxESS
 * cabinet, or a Powerwall 3 on the Tesla inverter, which *is* the inverter. The
 * hero shows one photo for these rather than implying two boxes on the wall.
 */
export function isIntegratedInverter(hardware) {
  const inverterName = hardware?.inverter?.name;
  const batteryName = hardware?.battery?.name;
  if (!(Number(hardware?.batteryQuantity) > 0)) return false;

  return (
    (inverterName === INVERTER.FOXESS && batteryName === BATTERY.FOXESS) ||
    (inverterName === INVERTER.TESLA && batteryName === BATTERY.POWERWALL)
  );
}
